import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Grid, makeStyles, Theme, Chip, CircularProgress } from '@material-ui/core';
import FilterList from '@material-ui/icons/FilterList';
import { useHistory, useParams, useRouteMatch } from 'react-router';
import { Pagination } from '@material-ui/lab';
import _ from 'lodash';
import { MatchListType, FilterOption, InvestorMatchOutput, EntrepreneurProfileBody, ErrorCode, Role, PAGE_SIZE } from '../../types/types';
import { MatchedInvestorTabPathParams, MatchedInvestorTabPathState } from '../../types/router';
import { StyledText } from '../../components/StyledText';
import Button from '../../components/Button';
import InvestorAccordion from '../../components/InvestorAccordion';
import FilterModal from '../../components/FilterModal';
import { useUser } from '../../contexts';

const UNFINISHED_PROFILE_MESSAGE = 'We can\'t find any matched investors until you fill out your profile.';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    margin: theme.spacing(1)
  },
  investorList: {
    '& > .MuiAccordion-root': {
      marginBottom: theme.spacing(2)
    }
  },
  summary: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center'
  },
  centerItems: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  filterContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%'
  },
  filterChipContainer: {
    display: 'flex',
    width: '100%',
    padding: theme.spacing(1, 0)
  },
  statusChip: {
    marginRight: theme.spacing(1)
  },
  filterButton: {
    width: 100
  }
}));

const usePaginationStyles = makeStyles((theme: Theme) => ({
  root: {
    marginTop: 20,
    '& .MuiPaginationItem-page.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: 'white'
    }
  }
}));


export interface TabState {
  listModel: InvestorMatchOutput[] | undefined,
  listError: string | undefined
}

interface MatchedInvestorTabProps {
  entProfile: EntrepreneurProfileBody | undefined
  listPage: number
  listFilter: FilterOption[]
  tabState: TabState
  pageIsLoading: boolean
  itemCount: number
  updatePageMap: (a: { list: MatchListType, page: number }) => void
  updateFilterMap: (a: { list: MatchListType, filters: FilterOption[] }) => void
  startReferral: (a: InvestorMatchOutput) => void
  refreshHandler: () => void
}

const MatchedInvestorTab: React.FC<MatchedInvestorTabProps> = ({
  entProfile,
  listPage, listFilter, tabState, pageIsLoading, itemCount,
  updatePageMap, updateFilterMap, startReferral, refreshHandler
}) => {
  const history = useHistory<MatchedInvestorTabPathState | undefined>();
  const pathParams = useParams<MatchedInvestorTabPathParams>();
  const { url } = useRouteMatch();

  const { listModel, listError } = tabState;

  const { userRole } = useUser();

  const { listType } = pathParams;

  const [isFilterModalOpen, setFilterModalOpen] = useState<boolean>(false);

  const styles = useStyles();
  const paginationStyles = usePaginationStyles();

  const pageCount = useMemo(() => Math.ceil(itemCount / PAGE_SIZE), [itemCount]);

  const renderList = useMemo(() => listModel || [], [listModel]);

  const renderFilterOptions = useMemo(() => {
    if (listFilter && listFilter.length >= 0) {
      return listFilter;
    } else {
      return [];
    }
  }, [listFilter]);

  const renderFilterButton = useMemo<boolean>(() => {
    if (renderFilterOptions.length === 0 && renderList.length === 0) {
      return false;
    } else {
      return true;
    }
  }, [renderFilterOptions, renderList]);

  const shouldRenderLoading = useMemo(() => (pageIsLoading && (!listModel || listModel.length === 0)), [listModel, pageIsLoading]);

  const renderEmptyText = useMemo(() => {
    if (shouldRenderLoading) {
      return null;
    }
    if (listError
      && listError === ErrorCode.InvalidMatchAlgorithmInput
      && entProfile
      && entProfile.editable) {
      return UNFINISHED_PROFILE_MESSAGE;
    }

    if (renderList.length === 0) {
      if (renderFilterOptions.length === 0) {
        if (listError !== undefined) {
          return 'Whoops, something went wrong.';
        }
        if (listType === MatchListType.recommended
          && listModel && itemCount > 0) {
          return 'Referrals have been made for all recommended investors.';
        }
        switch (listType) {
          case MatchListType.recommended:
            if (userRole === Role.Ent) {
              return 'There are no matching investors for your company.  To increase your chances of matching, make sure your company profile is up to date and as complete as possible.';
            }
            return 'No published investors in your network match with this company.';
          case MatchListType.requested:
            if (userRole === Role.Ent) {
              return 'You have not requested any referrals.';
            }
            return 'This company has not requested any referrals.';
          case MatchListType.sent:
            return 'No referrals have been sent.';
          default: return 'No matching investors found.';
        }
      }
      return 'No matching investors found.';
    }
    return null;
  }, [entProfile, shouldRenderLoading, listError, listModel, itemCount, listType, renderFilterOptions.length, renderList.length, userRole]);

  const handlePageChange = (_event: any, page: number) => {
    updatePageMap({ page, list: listType });
  };

  const handleFilterModalOpen = () => {
    setFilterModalOpen(true);
  };

  const handleFilterModalClose = () => {
    setFilterModalOpen(false);
  };

  const handleFilterRemove = useCallback((opt: FilterOption) => {
    const filterOpts = renderFilterOptions.filter((i) => i.key !== opt.key);
    updateFilterMap({
      list: listType,
      filters: filterOpts
    });
  }, [listType, renderFilterOptions, updateFilterMap]);

  // commenting this out for now as it is causing some clunkiness
  // looks like it was intended to bring user back to the previous scroll position on clicking back button
  // but not sure it is worth the degradation in overall experience
  /*
  useEffect(() => {
    const strY = localStorage.getItem(url);
    if (renderList && renderList.length > 0 && strY) {
      setTimeout(() => {
        const posY = strY ? parseFloat(strY) : 0;
        window.scrollTo(0, posY);
        localStorage.removeItem(url);
      }, 100);
    }
  }, [renderList, url]);

  useEffect(() =>
    () => {
      const target = document.body;
      const position = target.getBoundingClientRect();
      const posY = 0 - position.top;
      localStorage.setItem(url, posY.toString());
    },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [url]);
  */
  const handleFilterModalSubmit = useCallback((filterOpts: FilterOption[]) => {
    updateFilterMap({
      list: listType,
      filters: filterOpts
    });
    setFilterModalOpen(false);
  }, [listType, updateFilterMap]);

  return (
    <Grid item xs={12} className={styles.investorList}>

      {/* Mark: - Filter Section */}
      {renderFilterButton && (
      <div className={styles.filterContainer}>
        <FilterModal
          open={isFilterModalOpen}
          filterOptions={renderFilterOptions}
          listType={listType}
          onClose={handleFilterModalClose}
          onSubmit={handleFilterModalSubmit}
        />
        <div className={styles.filterButton}>
          <Button
            startIcon={<FilterList />}
            variant='text'
            color='secondary'
            type='button'
            onClick={handleFilterModalOpen}
          >
            Filter
          </Button>
        </div>
        <div className={styles.filterChipContainer}>
          {renderFilterOptions.map((c: FilterOption) => (
            <div className={styles.statusChip} key={c.key}>
              <Chip
                label={c.label}
                variant='outlined'
                color='default'
                onDelete={() => handleFilterRemove(c)}
              />
            </div>
          ))}
        </div>
      </div>
      )}

      {/* Mark: - Loading Section */}
      {shouldRenderLoading && (
        <div className={styles.centerItems}>
          <CircularProgress />
        </div>
      )}

      {/* Mark: - Main List Section */}
      {renderList.length > 0 && renderList.map((investor, index) => (
        <InvestorAccordion
          investor={investor}
          entrepreneurProfile={entProfile}
          name={`investor-${index}`}
          key={`investor-${index}`}
          matchListType={listType}
          referralHandler={startReferral}
          refreshHandler={refreshHandler}
        />
      ))}

      {/* Mark: - Pagination Section */}
      {renderEmptyText && (
        <StyledText component='div'>
          <p>{renderEmptyText}</p>
          {renderEmptyText === UNFINISHED_PROFILE_MESSAGE && (
            <Button onClick={() => { history.push(''); }}>
              Finish Profile
            </Button>
          )}
        </StyledText>
      )}

      {pageCount > 0 && (
        <Pagination
          classes={paginationStyles}
          defaultPage={1}
          page={listPage}
          count={pageCount}
          onChange={handlePageChange}
        />
      )}
    </Grid>
  );
};

export default MatchedInvestorTab;
